<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 class="modal-title">Add Note</h4>
        </div>
        <div class="modal-body">
            <form-wrapper :validator="$v.addNote" class="form-wrapper">
                <form-group name="role" class="custom-textarea">
                    <md-textarea v-focus v-model="form.note" @input="update" :md-debounce="100" />
                </form-group>

                <input
                    type="file"
                    multiple
                    @change="onChange"
                    ref="file"
                    accept=".pdf,.xlsx,.xls,.txt,.ppt,.pptx,.docx,.doc,.mp4, .png, .gif, .jpeg, .jpg, .bmp, .webp"
                />
                <div class="note-type-container">
                    <div class="type-field">
                        <label class="custom-label">Type</label>
                        <vue-select
                            :reduce="(x) => x.name"
                            label="name"
                            :options="noteTypes"
                            placeholder="Status"
                            v-model="form.type"
                            :searchable="$root.isDesktop"
                            :clearable="false"
                        ></vue-select>
                    </div>
                </div>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <md-button class="dialog-button md-primary" @click="saveNote">
                Save
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">
                Cancel
            </md-button>
        </div>
    </div>
</template>

<script>
import { BlockBlobClient } from '@azure/storage-blob';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage } from '@/helpers';
import { required } from 'vuelidate/lib/validators';
import { NOTE_TYPE_CONSTANTS, STOP_NOTE_TYPE_CONSTANTS } from '@/utils/constants';

export default {
    name: 'NotesModalMobile',
    mixins: [GeneralMixin],
    props: {
        shipmentId: {
            type: Number,
            default: null
        },
        stopId: {
            type: Number,
            default: null
        },
        tripId: {
            type: Number,
            default: null
        },
        orderId: {
            type: Number,
            default: null
        },
        customerId: {
            type: Number,
            default: null
        },
        resolve: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            form: {
                note: '',
                type: 'Stop Note'
            },
            selectedPhoto: null,
            photoPreview: null,
            filelist: [],
            images: [],
            imageList: [],
            imageUrls: [],
            documents: [],
            documentList: [],
            documentUrls: [],
            noteTypes: [...NOTE_TYPE_CONSTANTS]
        };
    },
    mounted() {
        if (this.shipmentId != null) {
            this.form.type = 'Shipment Note';
        } else if (this.tripId != null) {
            this.form.type = 'Trip Started';
            this.noteTypes = this.noteTypes.concat([...STOP_NOTE_TYPE_CONSTANTS]);
        } else if (this.orderId != null) {
            this.form.type = 'Order Note';
        } else if (this.customerId != null) {
            this.form.type = 'Customer Note';
        } else {
            this.form.type = this.stopId !== null ? 'Stop Note' : 'Other';
            this.noteTypes = this.noteTypes.concat([...STOP_NOTE_TYPE_CONSTANTS]);
        }
    },
    methods: {
        onChange() {
            this.filelist = [];
            this.checkFileSize([...this.$refs.file.files]);
        },
        checkFileSize(files) {
            files.forEach((file) => {
                if (file.size / 1024 > 15000) {
                    this.$notify({
                        message: `${file.name} exceeded the maximum size.`,
                        type: 'danger'
                    });
                } else {
                    const name = this.handleDuplicateFileNames(file);
                    const blob = file.slice(0, file.size, file.type);
                    this.filelist.push(new File([blob], name, { type: file.type }));
                }
            });
        },
        handleDuplicateFileNames(item) {
            const documentNames = [];
            this.filelist.forEach((element) => {
                documentNames.push(element.name.toLowerCase());
            });
            const file = item.name.split('.');
            const fileName = file[0];
            const fileExt = file[1];
            if (documentNames.includes(item.name.toLowerCase())) {
                let index = 1;
                let name = `${fileName} (${index}).${fileExt}`;
                while (documentNames.includes(name.toLowerCase())) {
                    index += 1;
                    name = `${fileName} (${index}).${fileExt}`;
                }
                return name;
            }
            return item.name;
        },
        async prepareDocuments() {
            // Extract only file names with the specified types
            const acceptedDocumentFormat = ['.pdf', '.xlsx', '.xls', '.txt', '.ppt', '.pptx', '.docx', '.doc', '.mp4'];
            const filteredFiles = this.filelist.filter((file) => {
                const name = file.name || '';
                return acceptedDocumentFormat.some((type) => name.toLowerCase().endsWith(type));
            });

            const items = [];
            filteredFiles.forEach((item) => {
                items.push({ photoUrl: URL.createObjectURL(item), file: item, type: item.type });
            });
            this.documents = filteredFiles;
            this.documentList = items;
        },
        async prepareImages() {
            const acceptedImageFormat = ['.png', '.gif', '.jpeg', '.jpg', '.bmp', '.webp'];

            const filteredFiles = this.filelist.filter((file) => {
                const name = file.name || '';
                return acceptedImageFormat.some((type) => name.toLowerCase().endsWith(type));
            });

            const items = [];
            filteredFiles.forEach((item) => {
                items.push({ photoUrl: URL.createObjectURL(item), file: item, type: item.type });
            });
            this.images = filteredFiles;
            this.imageList = items;
        },
        async saveNote() {
            this.prepareDocuments();
            this.prepareImages();

            const resp = await this.addNote();
            if (resp.data && (resp.data.photoUploadUrls || resp.data.documentUploadUrls)) {
                const uploadPromiseList = [];
                const uploadDocumentPromiseList = [];
                this.$_handleLoaderState(true, 'SAVING...');

                if (resp.data.photoUploadUrls) {
                    for (let i = 0; i < resp.data.photoUploadUrls.length; i++) {
                        uploadPromiseList.push(
                            this.onUploadImage(resp.data.photoUploadUrls[i], this.imageList[i].file)
                        );
                    }
                    Promise.all(uploadPromiseList).then(() => {
                        this.$_handleLoaderState(false);
                    });
                }

                if (resp.data.documentUploadUrls) {
                    for (let i = 0; i < resp.data.documentUploadUrls.length; i++) {
                        uploadDocumentPromiseList.push(
                            this.onUploadDocument(resp.data.documentUploadUrls[i], this.documentList[i].file)
                        );
                    }
                    Promise.all(uploadDocumentPromiseList).then(() => {
                        this.$_handleLoaderState(false);
                    });
                }
            }
        },
        // eslint-disable-next-line consistent-return
        async addNote() {
            // if (!this.$v.addNote.$invalid) {
            this.$_handleLoaderState(true);
            let api = `/api/stops/${this.stopId}/notes`;
            if (this.shipmentId != null) {
                api = `/api/shipments/${this.shipmentId}/notes`;
            } else if (this.tripId != null) {
                api = `/api/trips/${this.tripId}/notes`;
            } else if (this.orderId != null) {
                api = `/api/orders/${this.orderId}/notes`;
            } else if (this.customerId != null) {
                api = `/api/customers/${this.customerId}/notes`;
            }

            const photoFiles = this.imageList.map((item) => {
                return item.file.name;
            });

            const documentFiles = this.documentList.map((item) => {
                return item.file.name;
            });

            const payload = {
                method: 'post',
                data: {
                    note: this.form.note,
                    type: this.form.type,
                    photoFileNames: photoFiles,
                    DocumentFileNames: documentFiles
                }
            };
            try {
                const response = await handleRequests(api, payload);
                this.$notify({
                    message: 'Successfully added note.',
                    type: 'success'
                });
                this.resolve(this.form.note);
                this.$_handleLoaderState(false);
                return response;
            } catch (e) {
                const message = 'Could not add a new note.';
                showErrorMessage(this, message, e);
                this.$_handleLoaderState(false);
                this.resolve(false);
            }
            // }
        },
        update(value) {
            this.form.note = value;
        },
        async onUploadImage(notePhotoUrl, photofile) {
            const blockBlobClient = new BlockBlobClient(notePhotoUrl);

            try {
                await blockBlobClient.upload(photofile, photofile.size, {
                    blobHTTPHeaders: {
                        blobContentType: photofile.type
                    }
                });

                this.selectedPhoto = null;
                this.photoPreview = null;
            } catch (error) {
                this.$notify({
                    message: `Error in uploading photo (${error})`,
                    type: 'danger'
                });
            }
        },
        async onUploadDocument(noteDocumentUrl, documentfile) {
            const blockBlobClient = new BlockBlobClient(noteDocumentUrl);

            try {
                await blockBlobClient.upload(documentfile, documentfile.size, {
                    blobHTTPHeaders: {
                        blobContentType: documentfile.type
                    }
                });

                this.selectedPhoto = null;
                this.photoPreview = null;
            } catch (error) {
                this.$notify({
                    message: `Error in uploading document (${error})`,
                    type: 'danger'
                });
            }
        },
        async uploadDocumentSuccess(filelist) {
            const items = [];
            filelist.forEach((item) => {
                items.push({ photoUrl: URL.createObjectURL(item), file: item, type: item.type });
            });
            this.documents = filelist;
            this.documentList = items;
        },
        uploadDocumentRemove(index, filelist) {
            this.documentList.splice(index, 1);
            this.documents = filelist;
        },
        async uploadImageSuccess(formData, index, fileList) {
            const items = [];
            formData.forEach((item) => {
                items.push({ photoUrl: URL.createObjectURL(item), file: item });
            });
            this.images = fileList;
            this.imageList = this.imageList.concat(items);
        },
        beforeRemove(index, done, fileList) {
            this.imageList.splice(index, 1);
            this.images = fileList;
            done();
        },
        editImage(formData, index, fileList) {
            let file;

            formData.forEach((item) => {
                file = item;
            });
            this.imageList[index].photoUrl = URL.createObjectURL(file);
            this.imageList[index].file = file;
            this.images = fileList;
        }
    },
    validations: {
        addNote: {
            note: {
                required
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.action-btn {
    margin-right: 10px;
}
.action-btns {
    position: absolute;
    right: 20px;
}

.modal-container {
    max-width: 720px;
    // min-height: 500px;
    .modal-body {
        min-height: 400px;
        padding-bottom: 0;
        .form-wrapper {
            position: relative;
            .note-type-container {
                position: absolute;
                right: 0;
                top: -52px;
                z-index: 9;

                .type-field {
                    width: 200px;
                    .custom-label {
                        display: block;
                        margin-bottom: -8px;
                    }
                }
                @media (min-width: 768px) {
                    top: 8px;
                }
            }
        }
    }
    .stats {
        flex-grow: 8;
        .md-icon {
            color: #ffd900;
        }
    }
}
</style>
